import React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverTrigger, PopoverContent } from './popover';
import { Input } from './input';

/**
 * DateTimePicker — Calendar popover + time input.
 *
 * value: string in "YYYY-MM-DDTHH:mm" format (same as native datetime-local)
 * onChange: (newValue: string) => void
 *
 * Storing a plain string keeps the existing submit code unchanged.
 */
export const DateTimePicker = ({ value, onChange, placeholder = 'Pick a date & time' }) => {
  const [open, setOpen] = React.useState(false);

  const parsed = value ? new Date(value) : null;
  const dateOnly = parsed && !isNaN(parsed) ? parsed : null;
  const timeOnly = value && value.includes('T') ? value.split('T')[1].slice(0, 5) : '12:00';

  const handleDateSelect = (date) => {
    if (!date) return;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    onChange(`${yyyy}-${mm}-${dd}T${timeOnly}`);
  };

  const handleTimeChange = (newTime) => {
    if (!dateOnly) {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      onChange(`${yyyy}-${mm}-${dd}T${newTime}`);
      return;
    }
    const yyyy = dateOnly.getFullYear();
    const mm = String(dateOnly.getMonth() + 1).padStart(2, '0');
    const dd = String(dateOnly.getDate()).padStart(2, '0');
    onChange(`${yyyy}-${mm}-${dd}T${newTime}`);
  };

  const displayLabel = dateOnly
    ? `${dateOnly.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}  ·  ${timeOnly}`
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid="datetime-picker-trigger"
          className="w-full justify-start bg-black/50 border-purple-500/30 text-white hover:bg-purple-900/30 hover:text-white font-normal"
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-purple-400" />
          <span className={dateOnly ? 'text-white' : 'text-purple-400/60'}>{displayLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-0 bg-[#1a0b2e] border-purple-500/40 text-white"
        data-testid="datetime-picker-popover"
      >
        <div className="p-3">
          <Calendar
            mode="single"
            selected={dateOnly}
            onSelect={handleDateSelect}
            initialFocus
            className="bg-transparent text-white"
          />
          <div className="mt-3 pt-3 border-t border-purple-500/20 flex items-center gap-2">
            <span className="text-purple-300 text-xs uppercase tracking-wider">Time</span>
            <Input
              type="time"
              value={timeOnly}
              onChange={(e) => handleTimeChange(e.target.value)}
              data-testid="datetime-picker-time"
              className="bg-black/40 border-purple-500/30 text-white flex-1"
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setOpen(false)}
              data-testid="datetime-picker-done"
              className="text-purple-300 hover:text-white"
            >
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateTimePicker;
